'use client'

import AspectRatioCover from '@comp/AspectRatioCover'
import clsx from 'clsx'
import Link from 'next/link'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import ShowcaseCardLandscape from '@comp/page/common/ad/ad-card/ShowcaseCardLandscape'
import { formatDate } from '@lib/datetime'
import { article } from '@lib/route/news'
import { detail } from '@lib/route/topic'
import { Box, Button, Skeleton } from '@comp/material'
import { get, map, size } from 'lodash-es'
import { useLocale, useTranslations } from 'next-intl'
import GoldenAgentCard from './GoldenAgentCard'

export default function NewsStandard({
  data
}) {
  const multi = size(get(data, 'images', [])) >= 3

  if(data?.type === 'premium-agent-l') {
    return (
      <GoldenAgentCard data={data} slideClassName="max-w-[270px]" />
    )
  }

  if(data?.is_ad && data?.ad) {
    if(data.ad.type === 'premium-agent-l') {
      return (
        <GoldenAgentCard data={data.ad} slideClassName="max-w-[270px]" />
      )
    }
    
    return (
      <ShowcaseCardLandscape
        data={data.ad}
        pageType="news_index_Recommend"
      />
    )
  }

  return (
    multi
      ? <Multi data={data} />
      : <Single data={data} />
  )
}

export function Single({
  data,
  target = '_self',
  className = '',
  onLoad
}) {
  const locale = useLocale()

  return (
    <div className={clsx('bg-white p-pix-10 block md:p-pix-20', className)}>
      <Link
        href={article(data, locale)}
        target={target}
        className="flex flex-col gap-x-pix-20 gap-y-pix-10 group md:flex-row"
      >
        <div className="w-full md:w-pix-200">
          <AspectRatioCover
            src={data.cover_img}
            width={200}
            height={140}
            alt={data.subject}
            wrapperClassName="!aspect-[100/45] md:!aspect-[100/70]"
            onLoad={onLoad}
            loading={onLoad ? 'eager' : 'lazy'}
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-y-pix-4 md:gap-y-pix-10">
          <div className="text-[15px] font-medium line-clamp-2 md:text-f.9 group-hover:text-primary">{data.subject}</div>
          <div className="text-[13px] text-c66 line-clamp-2 md:text-f.7">{data.brief}</div>
          <div className="mt-auto flex justify-between text-c66 text-[13px] md:text-f.7">
            <span>{data.owner_name}</span>
            {
              data.publish_at && <time dateTime={formatDate(data.publish_at * 1000, 'YYYY/MM/DD HH:mm:ss')}>{formatDate(data.publish_at * 1000, 'YYYY/MM/DD HH:mm')}</time>
            }
          </div>
        </div>
      </Link>
      <TopicBar data={data} />
    </div>
  )
}

export function Multi({
  data
}) {
  const locale = useLocale()
  
  return (
    <div className="bg-white p-pix-10 group block md:p-pix-20">
      <Link href={article(data, locale)} className="space-y-pix-10 block">
        <div className="text-f.9 font-medium line-clamp-2 group-hover:text-primary">{data.subject}</div>
        <div className="flex gap-x-pix-10">
          {
            map(data.images, (item) => (
              <div className="flex-1 min-w-0" key={item}>
                <AspectRatioCover
                  src={item}
                  width={200}
                  height={140}
                  alt={data.subject}
                />
              </div>
            ))
          }
        </div>
        <div className="text-f.7 text-c66 line-clamp-2">{data.brief}</div>
        <div className="flex justify-between text-f.7 text-c66">
          <span>{data.owner_name}</span>
          {
            data.publish_at && <time dateTime={formatDate(data.publish_at * 1000, 'YYYY/MM/DD HH:mm:ss')}>{formatDate(data.publish_at * 1000, 'YYYY/MM/DD HH:mm')}</time>
          }
        </div>
      </Link>
      <TopicBar data={data} />
    </div>
  )
}

export function NewsStandardSkeleton() {
  return (
    <Box sx={{ display: 'flex', columnGap: 2 }}>
      <Skeleton variant="rounded" width={200} height={100} />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Skeleton variant="rounded" height={20} />
        <Box>
          <Skeleton variant="rounded" width="90%" height={14} />
          <Skeleton variant="rounded" width="40%" height={14} sx={{ marginTop: 0.5 }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Skeleton variant="rounded" width={60} height={14} />
          <Skeleton variant="rounded" width={80} height={14} />
        </Box>
      </Box>
    </Box>
  )
}

function TopicBar({
  data
}) {
  const locale = useLocale()
  const ts = useTranslations('news')

  return (
    data.topic && size(data.topic) > 0 && (
      <div className="flex items-center gap-x-pix-10 bg-cf8 px-pix-6 mt-pix-10 h-pix-30">
        <span className="text-f.6 text-white bg-error px-pix-8 py-pix-1 rounded-lg">{ts('topic')}</span>
        <div className="flex-1 min-w-0 line-clamp-1 text-f.7 font-medium">{get(data, 'topic.subject')}</div>
        <Button
          href={detail(data.topic, locale)}
          sx={() => ({
            p: 0,
            minWidth: 0,
            fontSize: '14px',
            '.MuiButton-endIcon': {
              ml: 0
            }
          })}
          variant="text"
          endIcon={<KeyboardArrowRightIcon />}
        >
          {ts('view')}
        </Button>
      </div>
    )
  )
}
